/**
 * VistoeraTASK-00139 — Canvas Generate-Video poster load concurrency gate.
 *
 * FORENSIC ROOT CAUSE (real Network capture on Preview, not guessed — see
 * task report): a Canvas with N historical `production_canvas` video
 * nodes whose poster derivative hasn't been backfilled yet fires N
 * `GET .../video-generation/tasks/{id}/poster` requests essentially at
 * once on mount (no client concurrency cap, no `loading="lazy"` on the
 * `<img>` before this fix). The backend's lazy poster-backfill route
 * (`ensure_output_poster`) does a synchronous R2 materialize + cv2 decode
 * gated by a fixed 2-permit *process-wide* semaphore
 * (`MediaDerivativeSlot`, VISTOERA_RENDER_MEDIA_MEMORY_SAFETY_P0 —
 * deliberately small for OOM safety, NOT a knob this task raises), on a
 * single Render uvicorn process with no `--workers`. A real capture of an
 * 11-video-node Canvas showed 10/11 `/poster` requests (and 12/13
 * unrelated `/tasks/{id}` metadata GETs sharing the same thread pool)
 * still had received *no response at all* after 28s. Each node whose
 * poster hasn't resolved sits in `IDLE_POSTER` with `posterUsable=false`,
 * which renders the (correctly non-black, intentionally animated)
 * `pcv-video-loading-shimmer` placeholder — and with no client-side cap,
 * that shimmer runs indefinitely on nearly every node. That indefinite
 * shimmer *is* the "flashing/loading" the user reported — never video
 * binary preloading (confirmed 0 initial video-binary requests/bytes).
 *
 * Fix: a small, dependency-free FIFO admission gate. Only
 * `MAX_CONCURRENT_POSTER_LOADS` poster `<img>` loads are ever allowed to
 * actually carry a `src` (i.e. actually fire an HTTP request) across the
 * whole Canvas at once; everything else queues until a slot frees on
 * load/error/release. This does not change the backend's fixed ceiling
 * (out of scope — memory safety) but stops the Canvas itself from ever
 * presenting more concurrent poster requests than that ceiling can
 * reasonably absorb in a reasonable time, which is what was starving
 * every node (and unrelated task-metadata reads) today.
 */

const MAX_CONCURRENT_POSTER_LOADS = 4

interface QueueEntry {
  resolve: () => void
}

let active = 0
const queue: QueueEntry[] = []

function pump(): void {
  while (active < MAX_CONCURRENT_POSTER_LOADS && queue.length > 0) {
    const entry = queue.shift()
    if (!entry) break
    active += 1
    entry.resolve()
  }
}

/** Resolves once a load slot is granted (FIFO). Always pair with exactly
 * one later {@link releasePosterLoadSlot} call — on `<img>` load, error,
 * OR unmount/prop-change before the slot was ever used. */
export function acquirePosterLoadSlot(): Promise<void> {
  return new Promise((resolve) => {
    queue.push({ resolve })
    pump()
  })
}

/** Frees a previously granted slot and admits the next queued waiter, if
 * any. Safe to call at most once per successful `acquirePosterLoadSlot`. */
export function releasePosterLoadSlot(): void {
  active = Math.max(0, active - 1)
  pump()
}

/** Test-only introspection/reset — never used by product code. */
export function __posterLoadGateStateForTests(): { active: number; queued: number } {
  return { active, queued: queue.length }
}

export function __resetPosterLoadGateForTests(): void {
  active = 0
  queue.length = 0
}
